/* eslint-disable */
import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { Close as CloseIcon, Schedule as ScheduleIcon } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useMutation } from "@apollo/client";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import { RESCHEDULE_POST } from "../../gql/mutations/scrapy";
import { usePopup } from "../../context/commonPop";

export default function RescheduleDialog({ post, onClose, onSuccess }) {
  const theme = useTheme();
  const { handleOpen } = usePopup();
  const [newDate, setNewDate] = useState(post ? dayjs(post.publishAt) : dayjs().add(1, "hour"));

  const [reschedulePost, { loading }] = useMutation(RESCHEDULE_POST);

  const handleSubmit = async () => {
    if (!newDate || !newDate.isValid()) {
      handleOpen(false, "Please select a valid date and time");
      return;
    }
    if (newDate.isBefore(dayjs())) {
      handleOpen(false, "Rescheduled time must be in the future");
      return;
    }
    try {
      await reschedulePost({
        variables: {
          postId: post.id || post._id,
          publishAt: newDate.toISOString(),
        },
      });
      handleOpen(true, "Post rescheduled successfully");
      onSuccess?.();
    } catch (e) {
      handleOpen(false, e.message || "Failed to reschedule post");
    }
  };

  if (!post) return null;

  return (
    <Dialog
      open={!!post}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <ScheduleIcon color="primary" fontSize="small" />
            <Typography variant="h6" fontWeight={700}>
              Reschedule Post
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Box mb={2}>
          <Typography variant="caption" color="text.secondary" display="block">
            Post
          </Typography>
          <Typography variant="subtitle2" fontWeight={600}>
            {post.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Currently scheduled: {dayjs(post.publishAt).format("MMM D, YYYY h:mm A")}
          </Typography>
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label="New Date & Time"
            value={newDate}
            onChange={(val) => setNewDate(val)}
            disablePast
            slotProps={{
              textField: {
                fullWidth: true,
                helperText: `Timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
              },
            }}
          />
        </LocalizationProvider>

        {newDate && newDate.isValid() && newDate.isAfter(dayjs()) && (
          <Box
            mt={2}
            sx={{
              backgroundColor: theme.palette.primary.light + "15",
              borderRadius: "8px",
              p: 1.5,
              border: `1px solid ${theme.palette.primary.light}`,
            }}
          >
            <Typography variant="body2" color="primary.main" fontWeight={500}>
              Will publish on {newDate.format("dddd, MMMM D, YYYY [at] h:mm A")}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Box flex={1} />
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", minWidth: 130 }}
        >
          {loading ? "Saving…" : "Reschedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
